import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, Plus, Save, Shield, X } from "lucide-react";
import { useEffect, useState } from "react";
import { DEFAULT_FINANCE_TABS, FINANCE_TABS, type FinanceTab, type UserRole } from "@chro/shared";
import { ApiError, api } from "../../lib/api";
import { COMPANY_ROLES, MODULE_TOGGLES, roleLabel, type AdminUser } from "./shared";
import { useSetActiveCompany } from "./use-set-active-company";

export function CreateCompanyUserForm({ companyId = null }: { companyId?: string | null } = {}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Exclude<UserRole, "global_admin">>("trader");
  const [customFinanceTabs, setCustomFinanceTabs] = useState(false);
  const [financeTabs, setFinanceTabs] = useState<FinanceTab[]>(DEFAULT_FINANCE_TABS);
  const [eexCodes, setEexCodes] = useState<string[]>([]);
  // Distinct EEX execution codes seen on this company's trades, offered as
  // suggestions. 403s when the company has no EEX module -> the field hides.
  const codesQ = useQuery({
    queryKey: ["eex-trader-codes", companyId],
    queryFn: () => api<{ codes: string[] }>("/eex/trader-codes", companyRequest(companyId)),
    retry: false,
  });
  const mutation = useMutation({
    mutationFn: () =>
      api("/admin/company-users", companyRequest(companyId, {
        method: "POST",
        body: JSON.stringify({ email, displayName, password, role, eexTraderCodes: role === "trader" || role === "company_admin" ? eexCodes : [], ...(role === "finance" && customFinanceTabs ? { eexFinanceTabs: financeTabs } : {}) }),
      })),
    onSuccess: () => {
      setEmail("");
      setDisplayName("");
      setPassword("");
      setRole("trader");
      setFinanceTabs(DEFAULT_FINANCE_TABS);
      setCustomFinanceTabs(false);
      setEexCodes([]);
      void queryClient.invalidateQueries({ queryKey: ["admin", "company-users"] });
      void queryClient.invalidateQueries({ queryKey: ["eex-trader-identities"] });
    },
  });
  const eexEligible = role === "company_admin" || role === "trader";
  return (
    <UserCreateForm
      email={email}
      setEmail={setEmail}
      displayName={displayName}
      setDisplayName={setDisplayName}
      password={password}
      setPassword={setPassword}
      role={role}
      setRole={setRole}
      customFinanceTabs={customFinanceTabs}
      setCustomFinanceTabs={setCustomFinanceTabs}
      financeTabs={financeTabs}
      setFinanceTabs={setFinanceTabs}
      error={mutation.error ? userMutationError(mutation.error) : undefined}
      busy={mutation.isPending}
      onSubmit={() => mutation.mutate()}
      eexCodes={codesQ.isSuccess && eexEligible ? eexCodes : undefined}
      setEexCodes={setEexCodes}
      codeSuggestions={codesQ.data?.codes ?? []}
    />
  );
}

export function CreateGlobalUserForm() {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      api("/admin/global-users", {
        method: "POST",
        body: JSON.stringify({ email, displayName, password }),
      }),
    onSuccess: () => {
      setEmail("");
      setDisplayName("");
      setPassword("");
      void queryClient.invalidateQueries({ queryKey: ["admin", "global-users"] });
    },
  });
  return (
    <UserCreateForm
      email={email}
      setEmail={setEmail}
      displayName={displayName}
      setDisplayName={setDisplayName}
      password={password}
      setPassword={setPassword}
      busy={mutation.isPending}
      onSubmit={() => mutation.mutate()}
    />
  );
}

function UserCreateForm(props: {
  email: string;
  setEmail(v: string): void;
  displayName: string;
  setDisplayName(v: string): void;
  password: string;
  setPassword(v: string): void;
  role?: Exclude<UserRole, "global_admin">;
  setRole?(v: Exclude<UserRole, "global_admin">): void;
  busy: boolean;
  customFinanceTabs?: boolean;
  setCustomFinanceTabs?(enabled: boolean): void;
  financeTabs?: FinanceTab[];
  setFinanceTabs?(tabs: FinanceTab[]): void;
  error?: string;
  onSubmit(): void;
  // Optional EEX execution-code linking (company-user form only). Rendered when
  // eexCodes is defined (i.e. the company has the EEX module + the role can trade).
  eexCodes?: string[];
  setEexCodes?(v: string[]): void;
  codeSuggestions?: string[];
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        props.onSubmit();
      }}
      className="ops-panel ops-admin-create-form space-y-2"
    >
      <div className="ops-admin-user-fields grid gap-2">
      <input
        value={props.email}
        onChange={(e) => props.setEmail(e.target.value)}
        placeholder="Email"
        type="email"
        className="bg-transparent border border-border rounded px-2 py-1.5 text-sm"
        required
      />
      <input
        value={props.displayName}
        onChange={(e) => props.setDisplayName(e.target.value)}
        placeholder="Display name"
        className="bg-transparent border border-border rounded px-2 py-1.5 text-sm"
      />
      <input
        value={props.password}
        onChange={(e) => props.setPassword(e.target.value)}
        placeholder="Temporary password"
        type="password"
        minLength={10}
        className="bg-transparent border border-border rounded px-2 py-1.5 text-sm"
        required
      />
      {props.role && props.setRole ? (
        <select
          aria-label="New user role"
          value={props.role}
          onChange={(e) => props.setRole?.(e.target.value as Exclude<UserRole, "global_admin">)}
          className="bg-transparent border border-border rounded px-2 py-1.5 text-sm"
        >
          {COMPANY_ROLES.map((r) => (
            <option key={r} value={r}>
              {roleLabel(r)}
            </option>
          ))}
        </select>
      ) : null}
      <button
        type="submit"
        disabled={props.busy}
        className="ops-primary inline-flex items-center justify-center gap-1 rounded bg-accent text-white px-3 py-1.5 text-sm disabled:opacity-50"
      >
        <Plus size={14} />
        Create
      </button>
      </div>
      {props.role === "finance" && props.setCustomFinanceTabs && <label className="flex items-center gap-2 text-xs text-muted"><input type="checkbox" checked={props.customFinanceTabs ?? false} disabled={props.busy} onChange={event => props.setCustomFinanceTabs?.(event.target.checked)} />Customise Finance tabs</label>}
      {props.role === "finance" && !props.customFinanceTabs && <p className="text-xs text-muted">Inherits Finance category defaults from the access matrix.</p>}
      {props.role === "finance" && props.customFinanceTabs && props.setFinanceTabs && <FinanceTabsField tabs={props.financeTabs ?? []} onChange={props.setFinanceTabs} disabled={props.busy} />}
      {props.role === "finance" && <p className="text-xs text-muted">View and export only. After creating this account, turn on its EEX module to grant access. The user must change their temporary password at first sign-in.</p>}
      {props.error && <p role="alert" className="text-xs text-negRed">{props.error}</p>}
      {props.eexCodes && props.setEexCodes ? (
        <EexCodesField
          codes={props.eexCodes}
          setCodes={props.setEexCodes}
          suggestions={props.codeSuggestions ?? []}
        />
      ) : null}
    </form>
  );
}

// Tag-style multi-entry for a new user's EEX execution codes. Type-or-pick (from
// a datalist of codes already seen on trades) then Add; each becomes a removable
// chip. Enter adds without submitting the surrounding form.
function EexCodesField({
  codes,
  setCodes,
  suggestions,
}: {
  codes: string[];
  setCodes(v: string[]): void;
  suggestions: string[];
}) {
  const [draft, setDraft] = useState("");
  const add = (raw: string) => {
    const code = raw.trim();
    if (!code) return;
    if (codes.some((c) => c.toLowerCase() === code.toLowerCase())) {
      setDraft("");
      return;
    }
    setCodes([...codes, code]);
    setDraft("");
  };
  const remove = (code: string) => setCodes(codes.filter((c) => c !== code));
  const available = suggestions.filter(
    (s) => !codes.some((c) => c.toLowerCase() === s.toLowerCase())
  );
  return (
    <div className="space-y-1.5">
      <div className="text-xs text-muted">
        EEX execution codes (optional) — trades carrying these auto-attribute to this user
      </div>
      {codes.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {codes.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 rounded bg-accent/15 text-accent px-2 py-0.5 text-xs font-mono"
            >
              {code}
              <button
                type="button"
                onClick={() => remove(code)}
                className="opacity-70 hover:opacity-100"
                aria-label={`Remove ${code}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add(draft);
            }
          }}
          placeholder="e.g. Maria-Florina Ghenu"
          list="eex-code-suggestions"
          className="flex-1 bg-transparent border border-border rounded px-2 py-1.5 text-sm"
        />
        <datalist id="eex-code-suggestions">
          {available.map((s) => (
            <option key={s} value={s} />
          ))}
        </datalist>
        <button
          type="button"
          onClick={() => add(draft)}
          disabled={!draft.trim()}
          className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </div>
  );
}

// Friendly text for a user-mutation error — turns the seat-limit 409 into a clear
// message instead of the raw "seat_limit_reached" code.
function userMutationError(err: unknown): string {
  if (err instanceof ApiError && err.status === 409) {
    const body = err.body as { error?: string; used?: number; allowed?: number } | undefined;
    if (body?.error === "seat_limit_reached") {
      return `Trader seat limit reached — the plan allows ${body.allowed} trader seat(s). Disable another trader's EEX access or add seats to the plan.`;
    }
  }
  return err instanceof Error ? err.message : String(err);
}

export function UserTable({
  users,
  kind,
  disabled,
  companyId = null,
}: {
  users: AdminUser[];
  kind: "company" | "global";
  disabled: boolean;
  companyId?: string | null;
}) {
  const queryClient = useQueryClient();
  const endpoint = kind === "global" ? "/admin/global-users" : "/admin/company-users";
  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api(`${endpoint}/${id}`, companyRequest(kind === "company" ? companyId : null, {
        method: "PATCH",
        body: JSON.stringify(body),
      })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
  if (disabled) return <div className="text-sm text-muted">Loading...</div>;
  return (
    <div className="space-y-2">
      <div className="ops-table-frame border border-border rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ops-admin-user-table w-full min-w-[980px] text-sm">
            <thead className="text-xs text-muted border-b border-border">
              <tr>
                <th className="text-left p-2">Email</th>
                <th className="text-left p-2">Name</th>
                <th className="text-left p-2">Role</th>
                <th className="text-left p-2">Modules</th>
                <th className="text-left p-2">EEX access</th>
                <th className="text-left p-2">Password</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  kind={kind}
                  busy={mutation.isPending}
                  mutate={mutation.mutate}
                />
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-3 text-sm text-muted">
                    No users.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {mutation.error ? (
        <div className="text-xs text-negRed">{userMutationError(mutation.error)}</div>
      ) : null}
    </div>
  );
}

function companyRequest(companyId: string | null, init: RequestInit = {}): RequestInit {
  return companyId
    ? { ...init, headers: { ...(init.headers as Record<string, string> | undefined), "x-company-id": companyId } }
    : init;
}

function UserRow({
  user,
  kind,
  busy,
  mutate,
}: {
  user: AdminUser;
  kind: "company" | "global";
  busy: boolean;
  mutate(args: { id: string; body: Record<string, unknown> }): void;
}) {
  const setActiveCompany = useSetActiveCompany();
  const [email, setEmail] = useState(user.email);
  const [displayName, setDisplayName] = useState(user.displayName ?? "");
  const [password, setPassword] = useState("");

  useEffect(() => {
    setEmail(user.email);
    setDisplayName(user.displayName ?? "");
  }, [user.email, user.displayName]);

  const trimmedEmail = email.trim();
  const trimmedDisplayName = displayName.trim();
  const hasProfileChanges =
    trimmedEmail !== user.email || trimmedDisplayName !== (user.displayName ?? "");

  function saveProfile() {
    if (!hasProfileChanges || !trimmedEmail) return;
    mutate({
      id: user.id,
      body: {
        email: trimmedEmail,
        displayName: trimmedDisplayName || null,
      },
    });
  }

  return (
    <tr className="border-b border-border/60 last:border-0">
      <td className="p-2">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          type="email"
          className="w-full min-w-52 bg-transparent border border-border rounded px-2 py-1 text-xs"
          required
        />
      </td>
      <td className="p-2">
        <input
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          placeholder="Display name"
          className="w-full min-w-44 bg-transparent border border-border rounded px-2 py-1 text-xs"
        />
      </td>
      <td className="p-2">
        {kind === "company" ? (
          <select
            value={user.role}
            onChange={(e) => mutate({ id: user.id, body: { role: e.target.value } })}
            disabled={busy}
            className="bg-transparent border border-border rounded px-2 py-1 text-xs disabled:opacity-50"
          >
            {COMPANY_ROLES.map((r) => (
              <option key={r} value={r}>
                {roleLabel(r)}
              </option>
            ))}
          </select>
        ) : (
          roleLabel(user.role)
        )}
      </td>
      <td className="p-2">
        {kind === "company" && user.role !== "company_admin" ? (
          <div className="flex flex-wrap gap-1">
            {MODULE_TOGGLES.filter((m) => user.role !== "finance" || m.key === "eexEnabled").map((m) => {
              const on = user[m.key];
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => mutate({ id: user.id, body: { [m.key]: !on } })}
                  disabled={busy}
                  className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs disabled:opacity-50 ${
                    on ? "border-posGreen/50 text-posGreen" : "border-border text-muted"
                  }`}
                >
                  {m.label} {on ? "on" : "off"}
                </button>
              );
            })}
          </div>
        ) : (
          <span className="text-xs text-muted">Always</span>
        )}
      </td>
      <td className="p-2">
        {kind === "company" && user.role === "finance" ? (
          <div className="space-y-2"><p className="text-xs text-muted">{Object.keys(user.eexAccessOverrides ?? {}).length ? `${Object.keys(user.eexAccessOverrides ?? {}).length} permission overrides` : "Inherits Finance category defaults"}</p><Link to="/eex/settings" search={{ section: "access" }} onClick={() => { if (user.companyId) setActiveCompany(user.companyId); }} className="text-xs text-accent hover:underline">Manage category and user access</Link></div>
        ) : kind === "company" && user.role !== "company_admin" ? (
          <select
            value={user.eexEditPermission}
            onChange={(e) => mutate({ id: user.id, body: { eexEditPermission: e.target.value } })}
            disabled={busy}
            className="bg-transparent border border-border rounded px-2 py-1 text-xs disabled:opacity-50"
          >
            <option value="none">Not permitted</option>
            <option value="requires_approval">Requires approval</option>
            <option value="free">Free</option>
          </select>
        ) : (
          <span className="text-xs text-muted">Free</span>
        )}
      </td>
      <td className="p-2">
        <div className="flex gap-2">
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={user.mustChangePassword ? "Change required" : "New temp password"}
            type="password"
            minLength={10}
            className="w-44 bg-transparent border border-border rounded px-2 py-1 text-xs"
          />
          <button
            type="button"
            onClick={() => {
              if (!password) return;
              mutate({ id: user.id, body: { password } });
              setPassword("");
            }}
            disabled={busy || !password}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted/10 disabled:opacity-50"
          >
            <Shield size={13} />
            Reset
          </button>
        </div>
      </td>
      <td className="p-2 text-right">
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={saveProfile}
            disabled={busy || !hasProfileChanges || !trimmedEmail}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted/10 disabled:opacity-50"
          >
            <Save size={13} />
            Save
          </button>
          <button
            type="button"
            onClick={() => mutate({ id: user.id, body: { disabled: !user.disabledAt } })}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs hover:bg-muted/10 disabled:opacity-50"
          >
            {user.disabledAt ? <Check size={13} /> : <X size={13} />}
            {user.disabledAt ? "Enable" : "Disable"}
          </button>
        </div>
      </td>
    </tr>
  );
}

function FinanceTabsField({ tabs, onChange, disabled }: { tabs: FinanceTab[]; onChange(tabs: FinanceTab[]): void; disabled: boolean }) {
  return <fieldset disabled={disabled} className="min-w-48 space-y-2 disabled:opacity-50">
    <legend className="text-xs font-medium">Finance tabs · view &amp; export</legend>
    <div className="flex flex-wrap gap-x-3 gap-y-2">{FINANCE_TABS.map((tab) => <label key={tab.id} className="inline-flex items-center gap-1.5 text-xs">
      <input type="checkbox" checked={tabs.includes(tab.id)} onChange={(event) => onChange(event.target.checked ? [...tabs, tab.id] : tabs.filter((id) => id !== tab.id))} />{tab.label}
    </label>)}</div>
    {!tabs.length && <p className="text-xs text-muted">No EEX tabs are accessible.</p>}
  </fieldset>;
}
